import type { FC, ReactNode } from 'react'
import type { MediaItem, NavLink } from '@/types'

import Image from 'next/image'
import { useRef } from 'react'

import { pad } from '@/lib/format'
import { MediaFrame } from '@/components/generals/media-frame'
import { MoreLink } from '@/components/generals/more-link'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { StatValue } from '@/components/generals/stat-value'

import {
    FULL_MOTION,
    gsap,
    useGSAP
} from '@/lib/gsap'

import { media } from '@/constants/media'
import { architectureNotes, clubhouseStats } from '@/constants/experience'

interface ClubhouseImages {
    arch: MediaItem
    wide: MediaItem
    tall: MediaItem
}

interface ClubhouseSectionProps {
    index?: string
    /** Swap the photographs, for example on the clubhouse page where the hero already uses the default. */
    images?: Partial<ClubhouseImages>
    more?: NavLink
}

export const ClubhouseSection: FC<ClubhouseSectionProps> = ({ index = '04', images, more }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    const photos: ClubhouseImages = {
        arch: images?.arch ?? media.clubhouseExterior,
        wide: images?.wide ?? media.clubhouseWide,
        tall: images?.tall ?? media.clubhouseTall
    }

    useGSAP(() => {
        const arch = sectionRef.current?.querySelector('[data-arch]')
        const archImage = sectionRef.current?.querySelector('[data-arch-image]')
        if (!arch || !archImage) return

        const mm = gsap.matchMedia()

        // The arched window widens as it rises into view.
        mm.add(FULL_MOTION, () => {
            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: arch,
                    start: 'top 90%',
                    end: 'center 45%',
                    scrub: true
                }
            })

            timeline
                .fromTo(arch, { clipPath: 'inset(10% 24% 0% 24% round 999px 999px 0px 0px)' }, {
                    clipPath: 'inset(0% 0% 0% 0% round 999px 999px 0px 0px)',
                    ease: 'none'
                }, 0)
                .fromTo(archImage, { scale: 1.32 }, { scale: 1, ease: 'none' }, 0)
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section
            ref={sectionRef}
            id='clubhouse'
            aria-labelledby='clubhouse-heading'
            className='relative z-10 bg-bone text-charcoal'
        >
            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-36'>
                <div className='grid gap-14 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-5 lg:pt-8'>
                        <SectionLabel index={index} className='text-charcoal/70'>
                            Clubhouse & architecture
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id='clubhouse-heading'
                            className='mt-6 text-[clamp(2.3rem,4.4vw,4.4rem)] font-light leading-[1.02] tracking-[-0.03em]'
                        >
                            Built from the ground <em className='font-serif font-normal'>it stands on.</em>
                        </SplitReveal>

                        <p data-reveal className='mt-8 max-w-md text-base leading-relaxed text-charcoal/75'>
                            The clubhouse is set into the slope above the 18th green, its walls pressed from the estate&apos;s own earth and its roof drawn as one long timber line. From the fairway it almost disappears. From the terrace you can see the whole course.
                        </p>

                        <ol className='mt-12 border-t border-charcoal/15'>
                            {architectureNotes.map((note, index) => (
                                <li key={note.title} data-reveal className='grid grid-cols-[3rem_1fr] gap-4 border-b border-charcoal/15 py-5'>
                                    <span className='pt-0.5 font-mono text-[11px] tabular-nums text-charcoal/50'>
                                        {pad(index + 1)}
                                    </span>

                                    <div>
                                        <h3 className='type-wide text-[15px] font-medium uppercase tracking-[0.04em]'>
                                            {note.title}
                                        </h3>

                                        <p className='mt-2 text-sm leading-relaxed text-charcoal/70'>
                                            {note.description}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        {more && (
                            <div data-reveal className='mt-10'>
                                <MoreLink href={more.href} label={more.label} />
                            </div>
                        )}
                    </div>

                    <div className='lg:col-span-7'>
                        <figure>
                            <div data-arch className='relative aspect-[4/5] overflow-hidden rounded-t-full bg-sand sm:aspect-[5/6] lg:aspect-[4/5]'>
                                <div data-arch-image className='absolute inset-0'>
                                    <Image
                                        src={photos.arch.src}
                                        alt={photos.arch.alt}
                                        fill
                                        sizes='(min-width: 1024px) 58vw, 100vw'
                                        className='object-cover'
                                        style={{ objectPosition: photos.arch.position }}
                                    />
                                </div>

                                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-pine/60 to-transparent' />

                                <p className='absolute bottom-5 left-5 right-5 flex justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-bone sm:bottom-7 sm:left-7 sm:right-7 sm:text-[11px]'>
                                    <span>
                                        The clubhouse
                                    </span>

                                    <span>
                                        Above the 18th green
                                    </span>
                                </p>
                            </div>
                        </figure>
                    </div>
                </div>

                <div className='mt-16 grid gap-8 lg:mt-28 lg:grid-cols-12 lg:items-end'>
                    <figure className='lg:col-span-8'>
                        <MediaFrame
                            image={photos.wide}
                            sizes='(min-width: 1024px) 66vw, 100vw'
                            mask='left'
                            parallax={8}
                            className='aspect-[16/10] rounded-[4px]'
                        />

                        <figcaption className='mt-3 flex justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground'>
                            <span>
                                The terrace on the cliff above the coast holes
                            </span>

                            <span className='hidden sm:inline'>
                                1,850 m² under one roof
                            </span>
                        </figcaption>
                    </figure>

                    <figure className='lg:col-span-4'>
                        <MediaFrame
                            image={photos.tall}
                            sizes='(min-width: 1024px) 33vw, 100vw'
                            mask='up'
                            parallax={6}
                            className='aspect-[4/5] rounded-[4px] lg:aspect-[3/4]'
                        />

                        <figcaption className='mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground'>
                            Arriving for a twilight round
                        </figcaption>
                    </figure>
                </div>

                <dl className='mt-16 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-charcoal/15 pt-10 lg:mt-24 lg:grid-cols-4'>
                    {clubhouseStats.map((stat) => (
                        <div key={stat.label} data-reveal className='flex flex-col-reverse'>
                            <dt className='mt-2 max-w-[14rem] text-sm leading-snug text-charcoal/65'>
                                {stat.label}
                            </dt>

                            <dd className='type-wide text-[clamp(2rem,3.6vw,3.4rem)] font-light leading-none tracking-[-0.03em]'>
                                <StatValue stat={stat} />
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
